import React from "react";
import { Link } from "react-router-dom";
import LogoChalk from "../../media/LogoChalk.png";
import { useLang } from "../../i18n/LanguageContext";
import { PiPushPinDuotone } from "react-icons/pi";

// Grain de l'ardoise (bruit SVG très léger)
const NOISE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.10 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")";

// Bois du cadre
const WOOD =
  "repeating-linear-gradient(90deg, rgba(0,0,0,0.05) 0 2px, transparent 2px 9px), linear-gradient(180deg, #8a5d34 0%, #6e4624 45%, #5a391c 100%)";

// Étoile à 4 branches (comme sur le logo)
const Star = ({ className = "", color = "#f2f0e8", size = 22, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} style={style} aria-hidden="true">
    <path
      d="M12 1.5 C12.8 8 16 11.2 22.5 12 C16 12.8 12.8 16 12 22.5 C11.2 16 8 12.8 1.5 12 C8 11.2 11.2 8 12 1.5 Z"
      fill={color}
      filter="url(#chalk)"
    />
  </svg>
);

const Line = ({ children, delay, className = "" }) => (
  <span className={`block chalk-text chalk-write ${className}`} style={{ animationDelay: delay }}>
    {children}
  </span>
);

const Blackboard = ({ slides = [] }) => {
  const { t } = useLang();
  return (
  <section className="on-dark bg-creme px-3 sm:px-6 pt-6 pb-12">
    {/* Filtre « craie » : bords légèrement irréguliers */}
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <filter id="chalk">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" result="t" />
        <feDisplacementMap in="SourceGraphic" in2="t" scale="2.4" xChannelSelector="R" yChannelSelector="G" />
      </filter>
    </svg>

    <div className="max-w-6xl mx-auto">
      {/* Cadre en bois */}
      <div
        className="relative rounded-[20px] p-3 sm:p-[18px] pb-7 sm:pb-9"
        style={{ background: WOOD, boxShadow: "0 18px 40px -12px rgba(43,22,80,0.35), inset 0 1px 0 rgba(255,255,255,0.18)" }}
      >
        {/* Ardoise */}
        <div
          className="relative overflow-hidden rounded-[10px] min-h-[440px] sm:min-h-[500px] flex flex-col"
          style={{
            backgroundImage: `${NOISE},
              radial-gradient(ellipse 38% 22% at 18% 30%, rgba(255,255,255,0.045), transparent 70%),
              radial-gradient(ellipse 30% 18% at 80% 70%, rgba(255,255,255,0.04), transparent 70%),
              radial-gradient(ellipse at 50% 45%, #30503b 0%, #253f2f 55%, #1b2e22 100%)`,
            boxShadow: "inset 0 0 0 2px rgba(0,0,0,0.35), inset 0 0 60px rgba(0,0,0,0.45)",
          }}
        >
          {/* Fantômes de craie (traces d'anciens cours) */}
          <span className="hidden md:block absolute top-8 left-10 font-chalk text-3xl text-craie/10 -rotate-6 select-none" aria-hidden="true">
            a² + b² = c²
          </span>
          <span className="hidden md:block absolute bottom-24 right-12 font-chalk text-3xl text-craie/10 rotate-3 select-none" aria-hidden="true">
            Bonjour · Hallo · Hello
          </span>
          <span className="hidden lg:block absolute top-1/2 left-8 font-chalk text-2xl text-craie/[0.07] -rotate-12 select-none" aria-hidden="true">
            H₂O
          </span>

          {/* Étoiles */}
          <Star className="absolute top-10 right-[14%] opacity-90" color="#f5a926" size={26} />
          <Star className="absolute top-24 right-[9%] opacity-70" color="#e7ece5" size={16} />
          <Star className="absolute bottom-28 left-[10%] opacity-80" color="#c9b6e6" size={20} />
          <Star className="hidden sm:block absolute top-[38%] left-[16%] opacity-60" color="#e7ece5" size={14} />

          {/* Annonces punaisées */}
          {slides.length > 0 && (
            <div className="marquee-pause relative z-10 mx-4 sm:mx-8 mt-4 flex items-center gap-3 px-4 py-2 rounded-md border border-dashed border-craie/30 text-craie/90 overflow-hidden">
              <PiPushPinDuotone className="shrink-0 text-or" size={20} aria-hidden="true" />
              <div className="whitespace-nowrap overflow-hidden font-chalk text-xl">
                <span className="inline-block animate-marquee">
                  {[...slides, ...slides].map((s, i) => (
                    <span key={i} className="mx-10">{s.text}</span>
                  ))}
                </span>
              </div>
            </div>
          )}

          {/* Écriture */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
            <img src={LogoChalk} alt="Tatie Soso" className="h-12 sm:h-16 w-auto mb-6 opacity-95 chalk-write" style={{ animationDelay: "0s" }} />

            <h1 className="font-chalk font-bold leading-[1.02] text-[clamp(2.7rem,7vw,5.4rem)]">
              <Line delay="0.35s">{t("Où tu veux,")}</Line>
              <Line delay="1.15s">{t("quand tu veux,")}</Line>
              <span className="relative inline-block">
                <Line delay="1.95s" className="chalk-text-or">{t("mais ensemble")}</Line>
                {/* Soulignement dessiné à la craie */}
                <svg viewBox="0 0 300 24" className="absolute left-0 -bottom-2 w-full h-5" preserveAspectRatio="none" aria-hidden="true">
                  <path
                    d="M4 15 C 60 4, 110 22, 160 12 S 250 6, 296 13"
                    fill="none"
                    stroke="#f5a926"
                    strokeWidth="4"
                    strokeLinecap="round"
                    className="chalk-draw"
                    style={{ animationDelay: "2.9s" }}
                    filter="url(#chalk)"
                  />
                </svg>
              </span>
            </h1>

            <p className="mt-7 max-w-xl font-chalk text-2xl sm:text-[1.7rem] text-craie/75 chalk-write" style={{ animationDelay: "3.1s" }}>
              {t("Des cours en petits groupes — plus on est nombreux, moins c'est cher.")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 chalk-fade" style={{ animationDelay: "3.4s" }}>
              <Link
                to="/cours_a_venir"
                className="px-8 py-3 rounded-full bg-or text-prune-deep font-bold text-base hover:bg-or-dark transition-colors shadow-lg"
              >
                {t("Voir les cours")}
              </Link>
              <Link
                to="/signin"
                className="px-8 py-3 rounded-full border-2 border-craie/70 text-craie font-bold text-base hover:bg-craie hover:text-ardoise transition-colors"
              >
                {t("Rejoindre la magie ✨")}
              </Link>
            </div>
          </div>
        </div>

        {/* Rebord avec craies et brosse */}
        <div className="absolute left-6 right-6 bottom-2 sm:bottom-3 h-3 sm:h-4 rounded-sm" style={{ background: "linear-gradient(180deg,#9a6a3e,#6b4423)", boxShadow: "0 3px 6px rgba(0,0,0,0.35)" }}>
          <span className="absolute -top-2 left-[12%] w-12 h-2.5 rounded-full bg-craie shadow" style={{ transform: "rotate(-4deg)" }} aria-hidden="true" />
          <span className="absolute -top-2 left-[20%] w-8 h-2.5 rounded-full bg-or shadow" style={{ transform: "rotate(6deg)" }} aria-hidden="true" />
          <span className="absolute -top-2 left-[24.5%] w-6 h-2.5 rounded-full shadow" style={{ background: "#c9b6e6", transform: "rotate(-2deg)" }} aria-hidden="true" />
          <span className="absolute -top-4 right-[14%] w-20 sm:w-24 h-5 rounded-md shadow-carte overflow-hidden" aria-hidden="true">
            <span className="block h-1/2 bg-prune" />
            <span className="block h-1/2 bg-stone-300" />
          </span>
        </div>
      </div>
    </div>
  </section>
  );
};

export default Blackboard;
